<template>
  <div class="w-full max-w-full min-w-0 overflow-x-hidden" dir="rtl">
    <div v-if="isAuthorized">
      <!-- Header + date filter -->
      <div class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden mb-4 w-full max-w-full min-w-0">
        <header class="px-3 sm:px-4 py-4 bg-gray-900/50 border-b border-gray-700 min-w-0">
          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
            <div class="min-w-0 overflow-hidden">
              <h3 class="text-sm font-bold text-gray-100 truncate">خلاصه محصولات</h3>
              <p class="text-[11px] text-gray-400 mt-1 truncate">
                فروش آنلاین و حضوری هر محصول این شریک، به همراه سود — روی هر محصول کلیک کن تا گزارش کاملش باز شود
              </p>
            </div>

            <button
                @click="fetchList"
                :disabled="listLoading"
                class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-gray-800 border border-gray-600 text-xs text-gray-200 hover:bg-gray-700 transition disabled:opacity-50 shrink-0"
            >
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': listLoading }"></i>
              بروزرسانی
            </button>
          </div>
        </header>

        <section class="px-3 sm:px-4 py-4 bg-gray-900/30 border-b border-gray-700 min-w-0 overflow-hidden">
          <div class="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 min-w-0">
            <div class="col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">از تاریخ</label>
              <date placeholder="از تاریخ" v-model="fromDate" />
            </div>

            <div class="col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">تا تاریخ</label>
              <date placeholder="تا تاریخ" v-model="toDate" />
            </div>

            <div class="col-span-2 lg:col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">جستجو با بارکد / GTIN</label>
              <input
                  type="text"
                  v-model="gtin"
                  placeholder="مثلاً 6221234567890"
                  dir="ltr"
                  class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-2 sm:px-3 py-2 text-[11px] sm:text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
              />
            </div>

            <div class="col-span-2 lg:col-span-2 flex items-end gap-2 min-w-0">
              <button
                  @click="applyDateFilter"
                  class="px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-xs text-white transition shrink-0"
              >
                اعمال فیلتر
              </button>
              <button
                  v-if="fromDate || toDate || gtin"
                  @click="clearDateFilter"
                  class="px-3 py-2 rounded-xl bg-gray-800 border border-gray-600 text-xs text-gray-300 hover:bg-gray-700 transition shrink-0"
              >
                پاک کردن فیلترها
              </button>
            </div>
          </div>
        </section>

        <!-- Summary cards -->
        <section v-if="summary" class="px-3 sm:px-4 py-4 grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 min-w-0">
          <div class="rounded-xl bg-blue-600/10 border border-blue-500/20 px-3 py-2 min-w-0 overflow-hidden">
            <p class="text-[10px] text-blue-300/80 truncate">فروش آنلاین</p>
            <p class="text-xs font-bold text-blue-300 mt-1 truncate" dir="ltr">{{ formatMoney(summary.online_revenue) }}</p>
            <p class="text-[10px] text-blue-300/60 mt-1 truncate">سود: {{ formatMoney(summary.online_profit) }}</p>
          </div>

          <div class="rounded-xl bg-purple-600/10 border border-purple-500/20 px-3 py-2 min-w-0 overflow-hidden">
            <p class="text-[10px] text-purple-300/80 truncate">فروش حضوری</p>
            <p class="text-xs font-bold text-purple-300 mt-1 truncate" dir="ltr">{{ formatMoney(summary.in_store_revenue) }}</p>
            <p class="text-[10px] text-purple-300/60 mt-1 truncate">سود: {{ formatMoney(summary.in_store_profit) }}</p>
          </div>

          <div class="rounded-xl bg-emerald-600/10 border border-emerald-500/20 px-3 py-2 min-w-0 overflow-hidden">
            <p class="text-[10px] text-emerald-300/80 truncate">جمع فروش</p>
            <p class="text-xs font-bold text-emerald-400 mt-1 truncate" dir="ltr">{{ formatMoney(summary.total_revenue) }}</p>
            <p class="text-[10px] text-emerald-300/60 mt-1 truncate">سود کل: {{ formatMoney(summary.total_profit) }}</p>
          </div>

          <div class="rounded-xl bg-gray-900/70 border border-gray-700 px-3 py-2 min-w-0 overflow-hidden">
            <p class="text-[10px] text-gray-500 truncate">تعداد فروخته‌شده</p>
            <p class="text-xs font-bold text-gray-200 mt-1 truncate" dir="ltr">{{ formatNumber(summary.total_quantity) }}</p>
            <p class="text-[10px] text-gray-500 mt-1 truncate">در {{ formatNumber(count) }} محصول</p>
          </div>
        </section>

        <!-- Charts -->
        <section v-if="summary && products.length" class="px-3 sm:px-4 pb-4 grid grid-cols-1 lg:grid-cols-2 gap-3 min-w-0">
          <div class="bg-gray-900/50 border border-gray-700 rounded-xl p-3 min-w-0">
            <h4 class="text-[11px] font-bold text-gray-300 mb-2">سهم فروش آنلاین / حضوری</h4>
            <div class="relative h-56"><canvas ref="channelChart"></canvas></div>
          </div>
          <div class="bg-gray-900/50 border border-gray-700 rounded-xl p-3 min-w-0">
            <h4 class="text-[11px] font-bold text-gray-300 mb-2">پرسودترین محصولات (کل حساب) — برای دیدن جزئیات کلیک کن</h4>
            <div class="relative h-56"><canvas ref="topProductsChart"></canvas></div>
          </div>
        </section>
      </div>

      <!-- Loading / error -->
      <div v-if="listLoading" class="p-10 text-center">
        <i class="fas fa-circle-notch fa-spin text-indigo-400 text-2xl"></i>
        <p class="text-gray-400 mt-3 text-sm">در حال دریافت اطلاعات...</p>
      </div>

      <div v-else-if="listError" class="p-10 text-center">
        <i class="fas fa-exclamation-triangle text-red-400 text-2xl"></i>
        <p class="text-red-400 mt-3 text-sm">{{ listError }}</p>
        <button @click="fetchList" class="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-xl text-white text-xs transition">
          تلاش مجدد
        </button>
      </div>

      <!-- Product list -->
      <div v-else class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden w-full max-w-full min-w-0">
        <header class="px-3 sm:px-4 py-3 bg-gray-900/50 border-b border-gray-700">
          <h4 class="text-xs font-bold text-gray-200">محصولات ({{ formatNumber(count) }})</h4>
          <p class="text-[10px] text-gray-500 mt-1">برای دیدن سابقه‌ی کامل فروش و سود، روی یک محصول کلیک کن</p>
        </header>

        <!-- Mobile cards -->
        <div class="md:hidden p-3 space-y-3 w-full max-w-full min-w-0 overflow-hidden">
          <article
              v-for="row in products"
              :key="row.product_id"
              @click="openProduct(row.product_id)"
              class="rounded-2xl hover:border-gray-600 p-3 shadow-sm w-full max-w-full min-w-0 overflow-hidden cursor-pointer transition border"
              :class="isAdminMode && row.cost_is_estimated ? 'bg-amber-500/5 border-amber-500/30' : 'bg-gray-900/45 border-gray-700/80'"
          >
            <div class="flex items-start justify-between gap-2 min-w-0">
              <div class="flex items-start gap-2 min-w-0">
                <div
                    class="shrink-0 w-10 h-10 aspect-square rounded-lg overflow-hidden border border-gray-700 bg-gray-800 flex items-center justify-center"
                    :class="imgOk(row.image) ? 'cursor-zoom-in' : ''"
                    @click.stop="zoomImage(row.image)"
                >
                  <img v-if="imgOk(row.image)" :src="img_source(row.image)" class="w-full h-full object-cover" @error="markBroken(row.image)">
                  <i v-else class="fas fa-image text-gray-600 text-sm"></i>
                </div>
                <p class="text-xs font-bold text-gray-100 leading-5 line-clamp-2 break-safe min-w-0">{{ row.product_name }}</p>
              </div>
              <div class="shrink-0 flex items-center gap-1">
                <span v-if="row.has_manual_discount" class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-medium border bg-rose-600/15 text-rose-300 border-rose-500/30">
                  <i class="fas fa-tag"></i> تخفیف دستی
                </span>
                <span v-if="isAdminMode && row.cost_is_estimated" class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-medium border bg-amber-600/15 text-amber-300 border-amber-500/30">
                  <i class="fas fa-info-circle"></i> بهای تخمینی
                </span>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-2 mt-3 min-w-0 text-[11px]">
              <div class="rounded-xl bg-blue-600/10 border border-blue-500/20 px-3 py-2 min-w-0 overflow-hidden">
                <p class="text-blue-300/70 truncate">آنلاین</p>
                <p class="text-blue-300 font-bold mt-1 truncate" dir="ltr">{{ formatMoney(row.online_revenue) }}</p>
              </div>
              <div class="rounded-xl bg-purple-600/10 border border-purple-500/20 px-3 py-2 min-w-0 overflow-hidden">
                <p class="text-purple-300/70 truncate">حضوری</p>
                <p class="text-purple-300 font-bold mt-1 truncate" dir="ltr">{{ formatMoney(row.in_store_revenue) }}</p>
              </div>
            </div>

            <div class="flex items-center justify-between mt-3 pt-3 border-t border-gray-700/70 text-[11px]">
              <span class="text-gray-500">تعداد: <b class="text-gray-300">{{ formatNumber(row.total_quantity) }}</b></span>
              <span class="font-bold" dir="ltr" :class="profitClass(row)">سود: {{ profitLabel(row) }}</span>
            </div>
          </article>

          <div v-if="products.length === 0" class="px-4 py-12 text-center rounded-2xl bg-gray-900/40 border border-gray-700">
            <i class="fas fa-box-open text-3xl text-gray-600 mb-3 block"></i>
            <p class="text-gray-400 text-sm">محصولی برای این شریک ثبت نشده</p>
          </div>
        </div>

        <!-- Desktop table -->
        <div class="hidden md:block w-full max-w-full min-w-0 overflow-x-auto table-scroll-safe">
          <table class="w-full min-w-[900px] text-sm">
            <thead class="bg-gray-900/70 border-b border-gray-700">
            <tr>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">محصول</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">تعداد آنلاین</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">فروش آنلاین</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">تعداد حضوری</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">فروش حضوری</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">سود کل</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">موجودی فعلی</th>
            </tr>
            </thead>

            <tbody class="divide-y divide-gray-700/50">
            <tr
                v-for="row in products"
                :key="row.product_id"
                @click="openProduct(row.product_id)"
                class="hover:bg-gray-700/30 transition cursor-pointer"
                :class="isAdminMode && row.cost_is_estimated ? 'bg-amber-500/5' : ''"
            >
              <td class="px-4 py-3">
                <div class="flex items-center gap-2 min-w-[220px] max-w-[320px] overflow-hidden">
                  <div
                      class="shrink-0 w-9 h-9 aspect-square rounded-lg overflow-hidden border border-gray-700 bg-gray-800 flex items-center justify-center"
                      :class="imgOk(row.image) ? 'cursor-zoom-in' : ''"
                      @click.stop="zoomImage(row.image)"
                  >
                    <img v-if="imgOk(row.image)" :src="img_source(row.image)" class="w-full h-full object-cover" @error="markBroken(row.image)">
                    <i v-else class="fas fa-image text-gray-600 text-xs"></i>
                  </div>
                  <span class="text-xs text-gray-100 font-medium truncate">{{ row.product_name }}</span>
                  <span v-if="row.has_manual_discount" title="این محصول حداقل یک فروش حضوری با تخفیف دستی داشته" class="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium border bg-rose-600/15 text-rose-300 border-rose-500/30">
                    <i class="fas fa-tag"></i>
                  </span>
                  <span v-if="isAdminMode && row.cost_is_estimated" title="بهای این محصول تخمینی است (بر اساس بهای فعلی، نه اسنپ‌شات لحظه‌ی فروش)" class="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium border bg-amber-600/15 text-amber-300 border-amber-500/30">
                    <i class="fas fa-info-circle"></i>
                  </span>
                </div>
              </td>
              <td class="px-4 py-3 text-xs text-gray-300 whitespace-nowrap text-right" dir="ltr">{{ formatNumber(row.online_quantity) }}</td>
              <td class="px-4 py-3 text-xs text-blue-300 whitespace-nowrap text-right" dir="ltr">{{ formatMoney(row.online_revenue) }}</td>
              <td class="px-4 py-3 text-xs text-gray-300 whitespace-nowrap text-right" dir="ltr">{{ formatNumber(row.in_store_quantity) }}</td>
              <td class="px-4 py-3 text-xs text-purple-300 whitespace-nowrap text-right" dir="ltr">{{ formatMoney(row.in_store_revenue) }}</td>
              <td class="px-4 py-3 text-xs font-bold whitespace-nowrap text-right" dir="ltr" :class="profitClass(row)">{{ profitLabel(row) }}</td>
              <td class="px-4 py-3 text-xs text-gray-400 whitespace-nowrap text-right" dir="ltr">{{ formatNumber(row.inventory_count) }}</td>
            </tr>

            <tr v-if="products.length === 0">
              <td colspan="7" class="px-4 py-12 text-center">
                <i class="fas fa-box-open text-3xl text-gray-600 mb-3 block"></i>
                <p class="text-gray-400 text-sm">محصولی برای این شریک ثبت نشده</p>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <footer v-if="count > 0" class="px-3 sm:px-4 py-4 bg-gray-900/30 border-t border-gray-700 min-w-0 overflow-hidden">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 min-w-0">
            <div class="text-[11px] sm:text-xs text-gray-400 text-center sm:text-right min-w-0">
              نمایش
              <b class="text-gray-200">{{ formatNumber(fromItem) }}</b>
              تا
              <b class="text-gray-200">{{ formatNumber(toItem) }}</b>
              از
              <b class="text-gray-200">{{ formatNumber(count) }}</b>
              محصول
            </div>

            <div class="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-2 min-w-0">
              <button
                  @click="goToPage(page - 1)"
                  :disabled="page === 1 || listLoading"
                  class="px-3 py-2 rounded-xl border text-xs transition disabled:opacity-50 disabled:cursor-not-allowed"
                  :class="page > 1 ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700' : 'bg-gray-900 border-gray-700 text-gray-500'"
              >
                قبلی
              </button>

              <span class="px-3 py-2 rounded-xl bg-gray-800 border border-gray-700 text-xs text-gray-300 text-center truncate">
                {{ formatNumber(page) }} / {{ formatNumber(totalPages) }}
              </span>

              <button
                  @click="goToPage(page + 1)"
                  :disabled="page >= totalPages || listLoading"
                  class="px-3 py-2 rounded-xl border text-xs transition disabled:opacity-50 disabled:cursor-not-allowed"
                  :class="page < totalPages ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700' : 'bg-gray-900 border-gray-700 text-gray-500'"
              >
                بعدی
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <div v-else class="w-full rounded-2xl border border-red-500/30 bg-red-950/40 px-4 py-5 text-right">
      <h3 class="text-sm font-bold text-red-100">دسترسی غیرمجاز</h3>
      <p class="mt-2 text-xs text-red-200">دسترسی برای شما مجاز نیست.</p>
    </div>

    <!-- Image zoom overlay -->
    <div
        v-if="zoomedImage"
        @click="zoomedImage = null"
        class="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6 cursor-zoom-out"
    >
      <img :src="zoomedImage" class="max-w-full max-h-full rounded-2xl shadow-2xl object-contain" @click.stop>
      <button
          @click="zoomedImage = null"
          class="absolute top-4 left-4 sm:top-6 sm:left-6 w-9 h-9 rounded-full bg-gray-900/80 border border-gray-600 text-gray-200 hover:bg-gray-800 transition flex items-center justify-center text-lg"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>
  </div>
</template>

<script>
import getPartnerCalculationsApi from './getPartnerCalculationsApi'
import date from "@/components/mcomponents/cleave/Date"

export default {
  name: 'PartnerCalculations',

  mixins: [getPartnerCalculationsApi],

  components: { date },

  props: {
    partnerId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      listLoading: false,
      listError: null,
      summary: null,
      products: [],
      topProducts: [],
      count: 0,

      page: 1,
      pageSize: 30,

      fromDate: '',
      toDate: '',
      gtin: '',

      channelChart: null,
      topProductsChart: null,

      zoomedImage: null,
      brokenImages: {},
    }
  },

  computed: {
    isAdminMode() {
      return !!this.partnerId
    },
    isAuthorized() {
      if (this.isAdminMode) return this.user.user_type === 'ma' || this.user.is_superuser
      return this.user.user_type === 'prtnr' || this.user.user_type === 'ma'
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.count / this.pageSize))
    },
    fromItem() {
      if (this.count === 0) return 0
      return (this.page - 1) * this.pageSize + 1
    },
    toItem() {
      return Math.min(this.page * this.pageSize, this.count)
    },
  },

  methods: {
    getListParams() {
      const params = { page: this.page, page_size: this.pageSize }
      if (this.fromDate) params.from = this.fromDate
      if (this.toDate) params.to = this.toDate
      if (this.gtin) params.gtin = this.gtin
      return params
    },

    fetchList() {
      this.listLoading = true
      this.listError = null

      const onSuccess = data => {
        this.summary = data.summary
        this.products = data.products || []
        this.topProducts = data.top_products || []
        this.count = data.count || 0
        this.listLoading = false
        this.$nextTick(() => this.renderCharts())
      }
      const onError = () => {
        this.listError = 'خطا در دریافت اطلاعات'
        this.listLoading = false
      }

      if (this.isAdminMode) {
        this.getAdminPartnerCalculationsList(this.partnerId, this.getListParams(), onSuccess, onError)
      } else {
        this.getPartnerCalculationsList(this.getListParams(), onSuccess, onError)
      }
    },

    applyDateFilter() {
      this.page = 1
      this.fetchList()
    },

    clearDateFilter() {
      this.fromDate = ''
      this.toDate = ''
      this.gtin = ''
      this.page = 1
      this.fetchList()
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.page) return
      this.page = page
      this.fetchList()
    },

    openProduct(productId) {
      if (this.isAdminMode) {
        this.$emit('select-product', productId)
        return
      }
      this.$router.push({ name: 'PartnerCalculationsProductDetail', params: { id: productId } })
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '۰'
      const number = Number(value)
      if (Number.isNaN(number)) return '۰'
      return new Intl.NumberFormat('fa-IR').format(number)
    },

    formatMoney(value) {
      return `${this.formatNumber(Math.round(Number(value || 0)))} تومان`
    },

    // اگه عکس واقعاً لود نشه (فایل حذف‌شده/لینک خراب)، v-if صرفاً وجود URL رو چک می‌کنه
    // نه لود شدنش -- برای همین با @error این URL رو به‌عنوان خراب علامت می‌زنیم تا آیکون
    // جایگزین نشون داده بشه، نه یک باکس خالی.
    imgOk(url) {
      return !!url && !this.brokenImages[url]
    },

    markBroken(url) {
      this.$set(this.brokenImages, url, true)
    },

    zoomImage(url) {
      if (this.imgOk(url)) this.zoomedImage = this.img_source(url)
    },

    // سود این ردیف، اگر بخشی از فروش آنلاینش «تخمینی» باشه (بدون اسنپ‌شاتِ نرخ ارز لحظه‌ی
    // فروش، با نرخ *فعلی* حساب شده)، ممکنه به‌خاطر نوسان نرخ ارز کاذب منفی نشون بده --
    // برای همچین ردیف‌هایی به‌جای عدد منفیِ نامطمئن «نامشخص» نشون می‌دیم. این پرچم فقط
    // برای مدیر کل نمایش داده می‌شه، طبق همون قاعده‌ای که «بهای تخمینی» رو از پنل خود
    // شریک مخفی نگه می‌داره.
    profitLabel(row) {
      const estimated = this.isAdminMode && row.cost_is_estimated
      if (estimated && Number(row.total_profit) < 0) return 'نامشخص'
      return this.formatMoney(row.total_profit)
    },

    profitClass(row) {
      const estimated = this.isAdminMode && row.cost_is_estimated
      if (estimated) {
        return Number(row.total_profit) < 0 ? 'text-amber-400' : 'text-amber-300'
      }
      return Number(row.total_profit) >= 0 ? 'text-emerald-400' : 'text-rose-400'
    },

    renderCharts() {
      if (typeof Chart === 'undefined') {
        console.error('Chart.js not loaded! Make sure to include Chart.js v2.9.4')
        return
      }
      this.renderChannelChart()
      this.renderTopProductsChart()
    },

    renderChannelChart() {
      const ctx = this.$refs.channelChart
      if (!ctx || !this.summary) return

      if (this.channelChart) this.channelChart.destroy()

      this.channelChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['آنلاین', 'حضوری'],
          datasets: [{
            data: [Number(this.summary.online_revenue || 0), Number(this.summary.in_store_revenue || 0)],
            backgroundColor: ['#3b82f6', '#a855f7'],
            borderWidth: 0,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: 'bottom',
            labels: { fontColor: '#9ca3af', fontFamily: 'Vazir, sans-serif', fontSize: 11 }
          },
          tooltips: {
            rtl: true,
            callbacks: {
              label: (item, data) => {
                const label = data.labels[item.index]
                const value = data.datasets[0].data[item.index]
                return `${label}: ${this.formatMoney(value)}`
              }
            }
          }
        }
      })
    },

    renderTopProductsChart() {
      const ctx = this.$refs.topProductsChart
      if (!ctx) return

      if (this.topProductsChart) this.topProductsChart.destroy()

      const topProducts = this.topProducts

      this.topProductsChart = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels: topProducts.map(p => (p.product_name || '').slice(0, 24)),
          datasets: [{
            label: 'سود',
            data: topProducts.map(p => Number(p.total_profit || 0)),
            backgroundColor: '#10b981',
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          onClick: (evt) => {
            const points = this.topProductsChart.getElementsAtEventForMode(evt, 'nearest', { intersect: true }, false)
            if (points.length) {
              const product = topProducts[points[0]._index]
              if (product) this.openProduct(product.product_id)
            }
          },
          onHover: (evt, elements) => {
            evt.target.style.cursor = elements.length ? 'pointer' : 'default'
          },
          tooltips: {
            rtl: true,
            callbacks: {
              label: (item) => this.formatMoney(item.xLabel)
            }
          },
          scales: {
            xAxes: [{
              ticks: {
                beginAtZero: true,
                fontColor: '#9ca3af',
                fontSize: 9,
                callback: value => this.formatNumber(value)
              },
              gridLines: { color: 'rgba(75, 85, 99, 0.2)' }
            }],
            yAxes: [{
              ticks: { fontColor: '#9ca3af', fontSize: 9 },
              gridLines: { display: false }
            }]
          }
        }
      })
    },
  },

  watch: {
    partnerId() {
      this.page = 1
      this.fetchList()
    },
  },

  mounted() {
    this.fetchList()
  },

  beforeDestroy() {
    if (this.channelChart) this.channelChart.destroy()
    if (this.topProductsChart) this.topProductsChart.destroy()
  }
}
</script>

<style scoped>
table {
  border-collapse: collapse;
}

.table-scroll-safe {
  scrollbar-width: thin;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.break-safe {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.min-w-0 {
  min-width: 0 !important;
}
</style>
